import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import type { CompanyData } from '@/modules/auth/helpers/CompanyStorage';
import { getCurrentCompanyId } from '@/modules/auth/helpers/CompanyStorage';
import type { TabData } from '@/__core/types/TabData';
import { RootState } from '@/setup';
import {
  hasPermissionInCompany,
  resolveCompanyPermissions,
  resolveFormPermissionCompanyId,
  type CompanyPermissionSummary,
  type ResolveCompanyPermissionsResult,
} from '../utils/moduleCompanyPermissions';
import { resolveEntityCompanyId, resolveTabCompanyId } from '../utils/moduleTabVisibility';
import { useModuleCompanyPermissions } from './useModuleCompanyPermissions';

export interface UseModuleToolbarPermissionsOptions {
  resource: string;
  activeCompanyFilter: string;
  /** Tab `type` values that use update permission (e.g. `update-employee`) */
  updateTabTypes?: string[];
}

export interface UseModuleToolbarPermissionsResult extends CompanyPermissionSummary {
  effectiveCompanyId: string | null;
  isGlobalScope: boolean;
  summary: CompanyPermissionSummary;
  hasPermission: (action: string) => boolean;
  hasActionPermission: (action: string, companyId?: string | null) => boolean;
  listPermissions: ReturnType<typeof useModuleCompanyPermissions>;
  resolvePermissionsForCompanyId: (
    companyId?: string | null
  ) => ResolveCompanyPermissionsResult;
  resolveTabPermissions: (tab: TabData) => ResolveCompanyPermissionsResult;
  canReadRecord: (record: { company_id?: unknown; company?: unknown }) => boolean;
}

const resolveFormCompanyIdFromTab = (tab: TabData): string | null => {
  const formData = tab.metadata?.formData;
  if (!formData || typeof formData !== 'object') {
    return null;
  }

  const record = formData as { company?: unknown; company_id?: unknown };
  const fromCompany = String(record.company ?? '').trim();
  if (fromCompany) return fromCompany;

  const fromCompanyId = String(record.company_id ?? '').trim();
  return fromCompanyId || null;
};

/**
 * Toolbar-scoped permissions for list modules: list CRUD from activeCompanyFilter,
 * plus helpers for tab/form/record resolution.
 */
export function useModuleToolbarPermissions(
  options: UseModuleToolbarPermissionsOptions
): UseModuleToolbarPermissionsResult {
  const authCompanies = useSelector(
    (state: RootState) => state.auth?.companies ?? []
  ) as CompanyData[];
  const globalRoles = useSelector((state: RootState) => state.auth?.globalRoles ?? []);
  const authSelectedCompany = useSelector(
    (state: RootState) => state.auth?.selectedCompany ?? null
  );
  const hasGlobalRoleFlag = useSelector(
    (state: RootState) => state.auth?.hasGlobalRole ?? false
  );

  const loginCompanyId = useMemo(
    () =>
      String(authSelectedCompany?.company_id || getCurrentCompanyId() || '').trim() ||
      null,
    [authSelectedCompany?.company_id]
  );

  const listPermissions = useModuleCompanyPermissions({
    resource: options.resource,
    companyId: options.activeCompanyFilter,
  });

  const permissionContext = useMemo(
    () => ({
      companies: authCompanies,
      resource: options.resource,
      globalRoles,
      selectedCompany: authSelectedCompany,
      hasGlobalRole: hasGlobalRoleFlag,
      loginCompanyId,
    }),
    [
      authCompanies,
      options.resource,
      globalRoles,
      authSelectedCompany,
      hasGlobalRoleFlag,
      loginCompanyId,
    ]
  );

  const resolvePermissionsForCompanyId = useCallback(
    (companyId?: string | null) =>
      resolveCompanyPermissions({
        ...permissionContext,
        companyId,
      }),
    [permissionContext]
  );

  const updateTabTypeSet = useMemo(
    () => new Set(options.updateTabTypes ?? []),
    [options.updateTabTypes]
  );

  const resolveTabPermissions = useCallback(
    (tab: TabData) => {
      const permissionCompanyId = resolveFormPermissionCompanyId({
        isEditMode: updateTabTypeSet.has(tab.type),
        activeCompanyFilter: options.activeCompanyFilter,
        tabCompanyId: resolveTabCompanyId(tab),
        formCompanyId: resolveFormCompanyIdFromTab(tab),
        entityCompanyId: resolveEntityCompanyId(tab.entityData),
      });

      return resolvePermissionsForCompanyId(permissionCompanyId);
    },
    [
      options.activeCompanyFilter,
      resolvePermissionsForCompanyId,
      updateTabTypeSet,
    ]
  );

  const hasActionPermission = useCallback(
    (action: string, companyId?: string | null) =>
      hasPermissionInCompany({
        ...permissionContext,
        companyId: companyId ?? options.activeCompanyFilter,
        action,
      }),
    [permissionContext, options.activeCompanyFilter]
  );

  const canReadRecord = useCallback(
    (record: { company_id?: unknown; company?: unknown }) => {
      const normalizedFilter = String(options.activeCompanyFilter || '').trim();
      if (
        normalizedFilter &&
        normalizedFilter !== 'all' &&
        normalizedFilter !== 'global'
      ) {
        return listPermissions.canRead;
      }

      return resolvePermissionsForCompanyId(resolveEntityCompanyId(record)).canRead;
    },
    [
      listPermissions.canRead,
      options.activeCompanyFilter,
      resolvePermissionsForCompanyId,
    ]
  );

  return {
    canRead: listPermissions.canRead,
    canCreate: listPermissions.canCreate,
    canUpdate: listPermissions.canUpdate,
    canDelete: listPermissions.canDelete,
    effectiveCompanyId: listPermissions.effectiveCompanyId,
    isGlobalScope: listPermissions.isGlobalScope,
    summary: listPermissions.summary,
    hasPermission: listPermissions.hasPermission,
    hasActionPermission,
    listPermissions,
    resolvePermissionsForCompanyId,
    resolveTabPermissions,
    canReadRecord,
  };
}

export default useModuleToolbarPermissions;
